const nestedExprRegex = /{{.*?}}/g;

// This regex matches JavaScript-style property and array access patterns.
// It starts with a valid variable name (`entityId`) and matches one or more
// valid property or array accesses, such as
//   - dot notation: obj.prop
//   - string property access: obj['prop']
//   - numeric array access: obj[0]
//   - optional chaining on any of the above: obj?.prop, obj?.['prop'], obj?.[0]
// The optional-chaining operator is matched here but normalized away by callers
// (see findAllPaths) so that a?.b and a.b resolve to the same dependency path.
const pathRegex =
  /(?<entityId>[$A-Z_a-z][\w$]*)(?:(?:\??\.(?![$A-Z_a-z][\w$]*\s*\()[$A-Z_a-z][\w$]*)|(?:(?:\?\.)?\['[^']*'])|(?:(?:\?\.)?\[\d+]))+/g;

const dynamicRegex = /^{{|}}$/g;

export const findAllPaths = (expr: string): { path: string; entityId: string }[] => {
  const paths: { path: string; entityId: string }[] = [];

  // Remove whitespace and outer curly braces
  const cleanExpr = expr.trim().replace(dynamicRegex, '');

  // Use matchAll to get all matches with capturing groups
  const matchesIterator = cleanExpr.matchAll(pathRegex);

  for (const match of matchesIterator) {
    // optional-chaining operators (?. and ?.[) are null-safety syntax, not part of the
    // property-path identity. normalize them to canonical dot/bracket access so an optional
    // path (b_1?.content) yields the same dependency as its plain form (b_1.content), and
    // matches the canonical paths the dependency-graph walker emits (which never contain ?.).
    const path = match[0].replace(/\?\.\[/g, '[').replace(/\?\./g, '.');
    // Using index-based access (match[1]) instead of named capture group (match.groups?.entityId)
    // This is for cross-platform compatibility, as named capture groups aren't fully supported
    // in all JavaScript environments (particularly React Native's JavaScriptCore on iOS)
    const entityId = match[1];

    if (entityId) {
      paths.push({ path, entityId });
    }
  }

  return paths;
};

export const findAllPathsWithNested = (
  inputString: string,
): { path: string; entityId: string }[] => {
  const allPaths: { path: string; entityId: string }[] = [];

  // Process the main expression
  allPaths.push(...findAllPaths(inputString));

  // Find and process all nested expressions
  const nestedMatches = inputString.match(nestedExprRegex);
  if (nestedMatches) {
    nestedMatches.forEach((match) => {
      allPaths.push(...findAllPaths(match));
    });
  }

  return allPaths;
};

export function extractPropertyPaths(
  inputString: string,
  isValidEntityId: (entityId: string) => boolean,
) {
  const result: Record<string, Set<string> | undefined> = {};

  // Get all paths using the memoized function
  const allPaths = findAllPathsWithNested(inputString);

  // Filter and organize paths by valid entity IDs
  for (const { path, entityId } of allPaths) {
    if (isValidEntityId(entityId)) {
      if (!result[entityId]) {
        result[entityId] = new Set();
      }
      result[entityId].add(path);
    }
  }

  return { result };
}
